import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

// ─── Modal ────────────────────────────────────────────────
export const Modal = ({ open, onClose, title, children, size = 'md', footer }) => {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal modal-${size}`}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

// ─── Confirm Dialog ───────────────────────────────────────
export const ConfirmDialog = ({ open, onClose, onConfirm, title, message, loading, variant = 'danger' }) => (
  <Modal open={open} onClose={onClose} title={title || 'Confirm Action'} size="sm"
    footer={<>
      <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
      <button className={`btn btn-${variant}`} onClick={onConfirm} disabled={loading}>
        {loading ? <span className="spinner spinner-sm" /> : 'Confirm'}
      </button>
    </>}>
    <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
      <AlertCircle size={20} color="var(--danger)" style={{flexShrink:0, marginTop:1}} />
      <p style={{fontSize:'var(--fs-sm)', color:'var(--text-secondary)'}}>{message}</p>
    </div>
  </Modal>
);

// ─── Table ────────────────────────────────────────────────
// rowStyle(row) -> optional extra inline style per row (e.g. tinting
// parent/component rows in the Asset List's tree view).
// stickyHeader -> the table scrolls inside its own box (both ways) and the header row stays in view
// while you scroll down, with the horizontal scroll bar always visible at the bottom of that box.
export const Table = ({ columns, data, loading, emptyText = 'No records found', onRowClick, rowStyle, stickyHeader = false }) => (
  <div className={stickyHeader ? 'table-wrap table-wrap-sticky' : 'table-wrap'}>
    <table className="ams-table">
      <thead>
        <tr>{columns.map((c, i) => (
          <th key={i} style={{ width: c.width, textAlign: c.align || 'left' }}>{c.title}</th>
        ))}</tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={columns.length}><div className="loading-center"><div className="spinner" /></div></td></tr>
        ) : !data?.length ? (
          <tr><td colSpan={columns.length}>
            <div className="empty-state"><p className="empty-state-title">{emptyText}</p></div>
          </td></tr>
        ) : data.map((row, ri) => (
          <tr key={row.id ?? ri} onClick={() => onRowClick && onRowClick(row)}
            style={{ ...(onRowClick ? { cursor: 'pointer' } : {}), ...(rowStyle ? rowStyle(row) : {}) }}>
            {columns.map((c, ci) => (
              <td key={ci} style={{ textAlign: c.align || 'left' }}>
                {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Pagination ───────────────────────────────────────────
export const Pagination = ({ page, totalPages, total, limit, onPage }) => (
  <div className="pagination">
    <span className="pagination-info">
      Showing {Math.min((page-1)*limit+1, total)}–{Math.min(page*limit, total)} of {total} records
    </span>
    <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
    <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page-1)}><ChevronLeft size={14}/></button>
    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
      let p = page <= 3 ? i+1 : page >= totalPages-2 ? totalPages-4+i : page-2+i;
      if (p < 1 || p > totalPages) return null;
      return <button key={p} className={`page-btn ${p===page?'active':''}`} onClick={() => onPage(p)}>{p}</button>;
    })}
    <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page+1)}><ChevronRight size={14}/></button>
    <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
  </div>
);

// ─── Form Field ───────────────────────────────────────────
export const Field = ({ label, required, error, hint, children }) => (
  <div className="form-group">
    {label && <label className="form-label">{label}{required && <span className="req">*</span>}</label>}
    {children}
    {error && <span className="form-error">{error}</span>}
    {hint && !error && <span className="form-hint">{hint}</span>}
  </div>
);

// ─── Input ────────────────────────────────────────────────
export const Input = React.forwardRef(({ error, ...props }, ref) => (
  <input ref={ref} className={`form-control${error?' error':''}`} {...props} />
));
Input.displayName = 'Input';

// ─── Select ───────────────────────────────────────────────
export const Select = React.forwardRef(({ error, children, placeholder, ...props }, ref) => (
  <select ref={ref} className={`form-control${error?' error':''}`} {...props}>
    {placeholder && <option value="">{placeholder}</option>}
    {children}
  </select>
));
Select.displayName = 'Select';

// ─── Textarea ─────────────────────────────────────────────
export const Textarea = React.forwardRef(({ error, rows=3, ...props }, ref) => (
  <textarea ref={ref} rows={rows} className={`form-control${error?' error':''}`} {...props} />
));
Textarea.displayName = 'Textarea';

// ─── Spinner / Loading ────────────────────────────────────
export const Spinner = ({ size = '' }) => <div className={`spinner${size?' spinner-'+size:''}`} />;

export const PageLoader = () => (
  <div className="loading-center" style={{ minHeight: 300 }}>
    <Spinner size="lg" />
  </div>
);

// ─── Tabs ────────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div className="tabs">
    {tabs.map(t => (
      <button key={t.key} className={`tab-btn${active===t.key?' active':''}`} onClick={() => onChange(t.key)}>
        {t.label}
        {t.count !== undefined && (
          <span style={{ marginLeft:6, background:'var(--bg)', borderRadius:10, padding:'1px 6px', fontSize:'var(--fs-xs)', color:'var(--text-muted)' }}>{t.count}</span>
        )}
      </button>
    ))}
  </div>
);

// ─── Badge ────────────────────────────────────────────────
export const Badge = ({ children, variant = 'gray' }) => (
  <span className={`badge badge-${variant}`}>{children}</span>
);

// ─── Alert ────────────────────────────────────────────────
export const Alert = ({ type = 'info', children }) => (
  <div className={`alert alert-${type}`}>
    <AlertCircle size={16} style={{flexShrink:0, marginTop:1}} />
    <span>{children}</span>
  </div>
);
export { default as AssetSearchInput } from './AssetSearchInput';
export { default as BulkUploadModal } from './BulkUploadModal';
